import { useEffect, useState, lazy, Suspense } from "react";
import Hero from "@/components/site/Hero";
import Marquee from "@/components/site/Marquee";
import Preloader from "@/components/site/Preloader";

// Below-the-fold sections are split into separate chunks so the initial
// bundle (hero + marquee) stays small and paints fast. They still mount
// on the same page so in-page anchor navigation keeps working.
const Services = lazy(() => import("@/components/site/Services"));
const Process = lazy(() => import("@/components/site/Process"));
const Work = lazy(() => import("@/components/site/Work"));
const TrackRecord = lazy(() => import("@/components/site/TrackRecord"));
const About = lazy(() => import("@/components/site/About"));
const Testimonials = lazy(() => import("@/components/site/Testimonials"));
const Contact = lazy(() => import("@/components/site/Contact"));
const Footer = lazy(() => import("@/components/site/Footer"));
const OfferBanner = lazy(() => import("@/components/site/OfferBanner"));
const Cursor = lazy(() => import("@/components/site/Cursor"));
const CookieConsent = lazy(() => import("@/components/site/CookieConsent"));
const FloatingDiscount = lazy(() => import("@/components/site/FloatingDiscount"));

function App() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!ready) return;
    let lenis;
    let raf;
    // Defer smooth-scroll init off the critical path so it never blocks first paint.
    const timer = setTimeout(async () => {
      const Lenis = (await import("lenis")).default;
      lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1, smoothWheel: true });
      window.__lenis = lenis;
      const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
      raf = requestAnimationFrame(loop);
    }, 0);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); if (lenis) lenis.destroy(); window.__lenis = null; };
  }, [ready]);

  // Block interaction/scroll while the intro preloader is on screen.
  useEffect(() => {
    if (ready) return;
    document.body.style.overflow = "hidden";
    window.scrollTo(0, 0);
    return () => { document.body.style.overflow = ""; };
  }, [ready]);

  return (
    <div className="aw-grain">
      <Preloader onDone={() => setReady(true)} />
      <Suspense fallback={null}>
        <CookieConsent />
        <Cursor />
        <FloatingDiscount />
      </Suspense>
      <Hero />
      <Marquee />
      <Suspense fallback={null}>
        <Services />
        <Process />
        <Work />
        <TrackRecord />
        <About />
        <div style={{ position: "relative", marginTop: "clamp(48px, 9vh, 120px)" }}>
          <div className="aw-gridfade" aria-hidden="true" />
          <div style={{ position: "relative", zIndex: 1 }}>
            <Testimonials />
            <section style={{ padding: "clamp(64px, 10vh, 120px) clamp(20px, 6vw, 120px) 0", maxWidth: 1400, margin: "0 auto" }}>
              <OfferBanner />
            </section>
            <Contact />
          </div>
        </div>
        <Footer />
      </Suspense>
    </div>
  );
}

export default App;
